import { useCallback, useEffect, useRef, useState } from "react"
import useProposalOperation from "./useProposalOperation"
import { UNCONFIRMED, listedMessage } from "./proposalMessages"
import { eventMovedBy, moveChanges, movedBy } from "./plannerCalendar"
import { dayLabel, daysLabel, otherName, warningRuleLabel } from "./plannerLabels"
import { answerMessage, answerTally, answersCount } from "./sendingLabels"

// Ticket 15 Q13, B3 pinned decisions: Delete (a draft never sent), Withdraw and a move take effect 7 s later, with Undo in the toast
// until then; a command waiting when another starts, or when the page closes, is sent at once. Reopen is sent at once. Commands leave
// one after the other, each at the revision shown, or at the one a command of this page saved since. Recording answers (C3 pinned
// decision Q1) also waits 7 s with Undo, bound to the revisions shown; a sending, a resending and a reminder are sent at once (their
// review is the confirmation, ticket 16). A published event's move waits 7 s too, after its dialog (E3-Q1).
export const UNDO_MS = 7000
// The moves a toast's Undo keeps from happening: a proposal's, and a published event's (Phase E3).
const MOVES = ["move", "moveEvent"]
// The move a toast's Undo would keep from happening: the proposal and its new start, whose warnings the toast names (Phase C1w). A
// published event's move has shown its neighbours' warnings in its dialog.
const waitingMove = current => (current.action === "move" ? { item: current.proposal, start: current.to.days.start } : null)
const COMMANDS = {
  delete: { path: "/admin/tournament-proposals/deletions", waiting: () => "will be deleted", done: () => "deleted." },
  withdraw: { path: "/admin/tournament-proposals/withdrawals", waiting: () => "will be withdrawn", done: () => "withdrawn." },
  reopen: { path: "/admin/tournament-proposals/reopenings", done: () => "reopened as a draft." },
  // A move is an edit of the dates (Phase A6): a Confirmată returns to Propusă ("Needs a new confirmation").
  move: { waiting: ({ to }) => `will move to ${daysLabel(to.days)}`,
    done: ({ to }, proposal) => `moved to ${daysLabel(to.days)}.${proposal.state === "confirmed" ? " It is Proposed again and needs a new confirmation." : ""}` },
  // A published event moved by whole weeks (Phase E3, ticket 14), with its stages and competition starts.
  moveEvent: { path: "/admin/competitive-calendar/event-moves", waiting: ({ to }) => `will move to ${daysLabel(to.days)}`,
    done: ({ to }) => `moved to ${daysLabel(to.days)}.` },
  // A batch of answers, named by its organizer (Waiting for answer) or its proposal (the editor's Answer tab).
  answer: { path: "/admin/tournament-proposals/answers",
    waiting: (extra, batch) => `: ${answersCount(batch.answers)} will be saved (${answerTally(batch.answers)})`,
    done: (extra, batch) => `: ${answersCount(batch.answers)} saved (${answerTally(batch.answers)}).`, kept: "the answers were not saved." },
}
// What a toast names: a proposal by its number, a published event by its own ("Event #39207"), a batch of answers by its organizer.
const subjectOf = target => (target.kind === "event" ? `Event #${target.eventId}` : target.number)
const kept = (action, proposal) => (COMMANDS[action].kept ? `${subjectOf(proposal)}: ${COMMANDS[action].kept}` : `${subjectOf(proposal)} kept as it was.`)
// "P-1001 will be deleted", "Aceline Sport SRL: 3 answers will be saved (…)".
const sentence = (proposal, words) => `${subjectOf(proposal)}${words.startsWith(":") ? "" : " "}${words}`

export default function usePlannerCommands({ onDone }) {
  const { pending, run } = useProposalOperation(listedMessage)
  const [toast, setToast] = useState(null)
  // The tournaments moved and not yet read again, by key: the revision they were moved at and where they go (plannerCalendar applyMoves).
  const [moves, setMoves] = useState({})
  // The proposals whose answer waits for its Undo or was sent, by id: the revision answered. Waiting for answer leaves them out.
  const [answered, setAnswered] = useState({})
  const unanswer = useCallback(answers => setAnswered(current => {
    const next = { ...current }
    answers.forEach(({ id, revision }) => { if (next[id] === revision) delete next[id] })
    return next
  }), [])
  const waiting = useRef(null)
  const queue = useRef(Promise.resolve())
  // The revision each proposal has after this page's own saved commands, which the calendar may not show yet; for a published event this
  // page moved, the dates revision its move left, by the one it was moved from.
  const savedRevisions = useRef({})
  const savedDates = useRef({})
  const mounted = useRef(true)
  const done = useRef(onDone)
  done.current = onDone

  const forget = useCallback((key, to) => setMoves(current => {
    if (!current[key] || (to && current[key].to !== to)) return current
    const { [key]: _gone, ...rest } = current
    return rest
  }), [])
  // A result's words; a command still waiting for its Undo keeps its toast, after them.
  const tell = useCallback(({ tone, text }) => setToast(waiting.current
    ? { tone: tone === "danger" ? "danger" : "info", text: `${text} ${waiting.current.text}`, undo: true, move: waitingMove(waiting.current) } : { tone, text }), [])
  // Every command leaves after the one before it has its result.
  const enqueue = useCallback(task => {
    queue.current = queue.current.then(task)
    return queue.current
  }, [])
  const send = useCallback((action, proposal, extra = {}) => enqueue(async () => {
    const command = COMMANDS[action]
    const revision = Math.max(proposal.revision, savedRevisions.current[proposal.id] || 0)
    const moved = action === "moveEvent" && savedDates.current[proposal.id]
    const datesRevision = moved && moved.from === proposal.datesRevision ? moved.to : proposal.datesRevision
    // Once the read shows the dates a move left, it names them itself.
    if (moved && moved.to === proposal.datesRevision) delete savedDates.current[proposal.id]
    // Answers are bound to the revisions shown: one changed since refuses the batch (ticket 16).
    const result = action === "move"
      ? await run("edit", "/admin/tournament-proposals/" + proposal.id, { revision, changes: moveChanges(proposal, extra.to) }, "patch")
      : action === "moveEvent" ? await run(action, command.path, { eventId: proposal.id, revision: datesRevision, weeks: extra.weeks,
        ...(extra.note && { note: extra.note }) })
        : action === "answer" ? await run(action, command.path, { answers: proposal.answers, ...(proposal.note && { note: proposal.note }) })
          : await run(action, command.path, { proposals: [{ id: proposal.id, revision }] })
    if (result?.status === "saved") (result.proposals || []).forEach(({ id, revision: saved }) => { savedRevisions.current[id] = saved })
    if (result?.status === "saved" && result.event) savedDates.current[proposal.id] = { from: datesRevision, to: result.event.datesRevision }
    if (!mounted.current || !result || result.status === "cancelled") return
    // A move not saved shows where the calendar has it; answers not saved come back to Waiting for answer.
    if (MOVES.includes(action) && result.status !== "saved") forget(proposal.key, extra.to)
    if (action === "answer" && result.status !== "saved") unanswer(proposal.answers)
    const refusal = action === "answer" && result.status === "error" ? answerMessage(result.code, result.details) : result.message
    tell(result.status === "saved" ? { tone: "success", text: sentence(proposal, command.done(extra, proposal)) }
      : { tone: "danger", text: `${subjectOf(proposal)}: ${result.status === "unknown" ? result.message || UNCONFIRMED : refusal}` })
    done.current(action, proposal, result)
  }), [enqueue, forget, run, tell, unanswer])
  // A command sent at once: `words.saved` in the toast when it is saved (none when its caller says it); a refusal in the toast after
  // `words.subject`, unless its dialog shows it (`refusalShownByCaller`). The promise of its result.
  const sendNow = useCallback((action, path, body, words) => enqueue(async () => {
    const result = await run(action, path, body)
    if (!mounted.current || !result || result.status === "cancelled") return result
    if (result.status === "saved") { if (words.saved) tell({ tone: "success", text: words.saved }) }
    else if (!words.refusalShownByCaller) tell({ tone: "danger", text: `${words.subject}: ${result.status === "unknown" ? result.message || UNCONFIRMED : result.message}` })
    done.current(action, null, result)
    return result
  }), [enqueue, run, tell])
  // Sending and answers (Phases C2a–C2b): one organizer's list, its last list again, a reminder; each dialog shows its refusal.
  const sendList = useCallback(body => sendNow("send", "/admin/tournament-proposals/sendings", body, { refusalShownByCaller: true }), [sendNow])
  const resend = useCallback((body, { name, how }) => sendNow("resend", "/admin/tournament-proposals/resendings", body, { refusalShownByCaller: true,
    saved: how === "email" ? `${name}'s list is on its way again.` : `${name}'s list is recorded as sent by you.` }), [sendNow])
  const remind = useCallback((body, { name }) => sendNow("remind", "/admin/tournament-proposals/reminders", body, { refusalShownByCaller: true,
    saved: `A reminder to ${name} is on its way.` }), [sendNow])
  // Publishing (Phase E1p): a batch of proposals, each its own operation with its outcome, which the publish result shows; and a
  // publication email sent again. Both are sent at once (the review's confirmation, ticket 18; there is no Undo).
  const publish = useCallback(body => sendNow("publish", "/admin/tournament-proposals/publications", body, { refusalShownByCaller: true }), [sendNow])
  const resendPublication = useCallback((body, { name, how }) => sendNow("resendPublication", "/admin/tournament-proposals/publication-resendings", body,
    { refusalShownByCaller: true, saved: how === "email" ? `The publication email to ${name} is on its way again.`
      : `The publication email to ${name} is recorded as sent by you.` }), [sendNow])
  // Acknowledging a warning (ticket 13 Q10) and undoing it, and the declared days off (ticket 15 Q15): sent at once. The dialogs show
  // their own refusals; an Undo acknowledge tells its refusal in the toast.
  const acknowledge = useCallback((item, warning, note) => sendNow("acknowledgeWarning", "/admin/tournament-proposal-acknowledgements",
    { proposalId: item.id, key: warning.key, fingerprint: warning.fingerprint, note: note || null },
    { refusalShownByCaller: true, saved: warning.other?.kind === "proposal" ? `Acknowledged on ${item.number} and ${otherName(warning.other)}. It no longer counts.`
      : `Acknowledged on ${item.number}. It no longer counts.` }), [sendNow])
  const unacknowledge = useCallback((item, warning) => sendNow("unacknowledgeWarning", "/admin/tournament-proposal-acknowledgements/deletions",
    { key: warning.key, acknowledgementId: warning.acknowledgement.id },
    { subject: item.number, saved: `${warningRuleLabel(warning.rule)} on ${item.number} counts again.` }), [sendNow])
  const addDayOff = useCallback(fields => sendNow("addDayOff", "/admin/tournament-proposal-days-off", fields,
    { refusalShownByCaller: true, saved: `Declared day off added: ${fields.name}, ${dayLabel(fields.date)}.` }), [sendNow])
  const removeDayOff = useCallback(dayOff => sendNow("removeDaysOff", "/admin/tournament-proposal-days-off/deletions",
    { daysOff: [{ id: dayOff.id, revision: dayOff.revision }] },
    { refusalShownByCaller: true, saved: `Declared day off removed: ${dayOff.name}, ${dayLabel(dayOff.date)}.` }), [sendNow])
  // The command waiting for its Undo, sent now: when the time is up, when another one starts, or when the page closes.
  const flush = useCallback(() => {
    const current = waiting.current
    if (!current) return
    clearTimeout(current.timer)
    waiting.current = null
    send(current.action, current.proposal, current)
  }, [send])
  const later = useCallback((action, proposal, extra = {}) => {
    flush()
    const text = `${sentence(proposal, COMMANDS[action].waiting(extra, proposal))}.`
    waiting.current = { action, proposal, text, ...extra, timer: setTimeout(flush, UNDO_MS) }
    if (MOVES.includes(action)) {
      setMoves(current => ({ ...current, [proposal.key]: { revision: action === "moveEvent" ? proposal.datesRevision : proposal.revision, to: extra.to } }))
    }
    if (action === "answer") setAnswered(current => ({ ...current, ...Object.fromEntries(proposal.answers.map(({ id, revision }) => [id, revision])) }))
    setToast({ tone: "info", text, undo: true, move: waitingMove(waiting.current) })
  }, [flush])
  const undo = useCallback(() => {
    const current = waiting.current
    if (!current) return
    clearTimeout(current.timer)
    waiting.current = null
    if (MOVES.includes(current.action)) forget(current.proposal.key)
    if (current.action === "answer") unanswer(current.proposal.answers)
    setToast({ tone: "info", text: kept(current.action, current.proposal) })
  }, [forget, unanswer])
  // A move of `delta` days, from where the proposal shows. Another move of the same proposal while it waits replaces the first, and
  // one back to where it was is an Undo.
  const move = useCallback((proposal, delta, timeZone) => {
    const current = waiting.current
    const to = movedBy(proposal, delta, timeZone)
    if (current?.action !== "move" || current.proposal.id !== proposal.id) return later("move", proposal, { to })
    if (to.days.start === current.proposal.days.start) return undo()
    clearTimeout(current.timer)
    waiting.current = null
    return later("move", current.proposal, { to })
  }, [later, undo])
  // A published event moved by `weeks` once its dialog is confirmed (Phase E3, E3-Q1), with the dialog's note: sent after 7 s, and Undo or
  // Ctrl+Z until then sends nothing. Another move of the same event while it waits is one move from where it was, and one back there is
  // an Undo.
  const moveEvent = useCallback((event, weeks, note, timeZone) => {
    const current = waiting.current
    if (current?.action !== "moveEvent" || current.proposal.id !== event.id) {
      return later("moveEvent", event, { to: eventMovedBy(event, weeks, timeZone), weeks, note })
    }
    const weeksInAll = current.weeks + weeks
    if (!weeksInAll) return undo()
    clearTimeout(current.timer)
    waiting.current = null
    return later("moveEvent", current.proposal, { to: eventMovedBy(current.proposal, weeksInAll, timeZone), weeks: weeksInAll, note: note || current.note })
  }, [later, undo])
  // Ctrl+Z: the last move, while it waits (ticket 15 Q14).
  const undoMove = useCallback(() => {
    if (!MOVES.includes(waiting.current?.action)) return false
    undo()
    return true
  }, [undo])
  const dismiss = useCallback(() => setToast(null), [])
  const flushOnLeave = useRef(flush)
  flushOnLeave.current = flush
  useEffect(() => () => { mounted.current = false; flushOnLeave.current() }, [])

  return {
    toast, pending, moves, answered, undo, undoMove, dismiss, tell, move, moveEvent, acknowledge, unacknowledge, addDayOff, removeDayOff, sendList, resend, remind,
    publish, resendPublication,
    remove: proposal => later("delete", proposal), withdraw: proposal => later("withdraw", proposal), reopen: proposal => send("reopen", proposal),
    // `{ number: who it is for, answers: [{ id, revision, decision, name? }], note? }`.
    answer: batch => later("answer", batch),
  }
}
